/**
 * The React half of the turn machine: one hook that owns a turn's whole lifetime.
 *
 * Everything below it is testable without React — the parser is a string transform, the
 * store is a reducer, `commitTurn` is a cache write. This file is where they are wired to a
 * component tree, and it is deliberately the only place that knows all four exist.
 *
 * Two behaviours here are load-bearing and easy to lose in a refactor:
 *
 * - **A running turn does not lock the interface**. This hook returns turn state; it
 * never sets a global `isLoading`. The only thing a turn disables is submitting a second
 * action. Disabling the entire application for ~50 seconds is the specific bug
 * the streaming design exists to delete.
 * - **Nothing aborts the turn.** Not unmount, not navigation, not unload. The server
 * finishes and commits regardless, so aborting buys nothing and costs the stream
 * that would have shown the result. The store lives at module scope precisely so a
 * remounting component rejoins the turn already in progress.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import type { ActiveTurn } from '@/api/generated';
import { commitTurn } from '@/turn/commit';
import { ACTIVE_TURN_POLL_MS, reconcileTurn } from '@/turn/reconcile';
import { newIdempotencyKey, runTurn, TURN_CLIENT_TIMEOUT_MS } from '@/turn/runTurn';
import { IDLE_TURN, mayStillBeRunning, useTurnStore, type TurnState } from '@/turn/store';

/**
 * The server pings every 15s (`sse_heartbeat_seconds`), so 40s of silence is three missed
 * heartbeats — long enough not to fire on a hiccup, short enough to say something before the
 * player concludes the game is broken. It is a *message*, never an abort: Phase A
 * legitimately thinks for tens of seconds (`src/quest-v2-api/LLM-API-FINDINGS.md` §6, p50 ~50s).
 */
export const TURN_SILENCE_WARNING_MS = 40_000;

export interface TurnController extends TurnState {
  /** A turn is resolving: ours, or one this client found already running (P3). */
  isResolving: boolean;
  /** False while a turn resolves, or before a story id exists. Nothing else disables it. */
  canSubmit: boolean;
  /** The turn another tab (or a previous session) has in flight, when the API said so. */
  activeTurn: ActiveTurn | null;
  /** `null` runs the opening turn — legal only at `chapter_count == 0`. */
  submit: (action: string | null) => void;
  /** Clear a failure the player has read, without touching a turn that may still be running. */
  dismiss: () => void;
}

export function useTurn(storyId: string | undefined): TurnController {
  const client = useQueryClient();
  const turn = useTurnStore();

  // P2's key, kept across attempts of the *same* action. A retry then replays the chapter
  // the first attempt produced instead of running — and paying for — a second turn. Cleared
  // on success, so the next action always gets a fresh key.
  const attempt = useRef<{ action: string | null; key: string } | null>(null);

  const resolving = mayStillBeRunning(turn);
  // A turn belonging to another story must not render into this one's feed — but it still
  // blocks submitting, because one client runs one turn at a time.
  const view: TurnState = turn.storyId === null || turn.storyId === storyId ? turn : IDLE_TURN;

  const submit = (action: string | null): void => {
    if (!storyId) return;
    const state = useTurnStore.getState();
    if (mayStillBeRunning(state)) return;

    const previous = attempt.current;
    const key = previous && previous.action === action ? previous.key : newIdempotencyKey();
    attempt.current = { action, key };

    state.begin({ storyId, action });

    void (async () => {
      try {
        const terminal = await runTurn({
          storyId,
          action,
          idempotencyKey: key,
          timeoutMs: TURN_CLIENT_TIMEOUT_MS,
          onEvent: (event) => useTurnStore.getState().apply(event),
          onActivity: (at) => useTurnStore.getState().markActivity(at),
        });
        attempt.current = null;
        await commitTurn(client, storyId, terminal.data);
        useTurnStore.getState().reset();
      } catch (cause) {
        useTurnStore.getState().fail(asApiError(cause));
      }
    })();
  };

  useTurnRecovery(storyId, resolving && turn.status !== 'streaming');
  useKilledStreamCheck(storyId, resolving);

  return {
    ...view,
    isResolving: resolving,
    canSubmit: Boolean(storyId) && !resolving,
    activeTurn: view.error?.activeTurn ?? null,
    submit,
    dismiss: () => {
      if (!mayStillBeRunning(useTurnStore.getState())) useTurnStore.getState().reset();
    },
  };
}

export interface TurnClock {
  /** Since the turn was submitted. What the elapsed counter shows. */
  elapsedMs: number;
  /** Since the last byte of *anything*, heartbeat included. */
  silentMs: number;
  /** Three missed heartbeats: say something, but do not abort and do not act. */
  stalled: boolean;
}

/**
 * The turn's two clocks, ticked once a second while a turn streams and frozen otherwise.
 *
 * They answer different questions and the live turn uses both. *Elapsed* is the honest thing to show a
 * player fifteen seconds into a fifty-second wait — a real number, in place of the fake
 * progress bar that is ruled out for a duration nobody can predict. *Silent* is a health
 * check on the connection rather than on the DM: the server pings every 15s
 * (`sse_heartbeat_seconds`), so a long quiet Phase A with heartbeats in it is a normal turn,
 * however long it runs, and only the pings stopping means something is wedged.
 */
export function useTurnClock(): TurnClock {
  const status = useTurnStore((state) => state.status);
  const startedAt = useTurnStore((state) => state.startedAt);
  const lastByteAt = useTurnStore((state) => state.lastByteAt);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (status !== 'streaming') return;
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [status]);

  if (status !== 'streaming') return { elapsedMs: 0, silentMs: 0, stalled: false };

  const elapsedMs = startedAt === null ? 0 : Math.max(0, now - startedAt);
  const silentMs = lastByteAt === null ? 0 : Math.max(0, now - lastByteAt);
  return { elapsedMs, silentMs, stalled: silentMs >= TURN_SILENCE_WARNING_MS };
}

/** Poll the server while a turn we cannot see may still be resolving (P3). */
function useTurnRecovery(storyId: string | undefined, active: boolean): void {
  const client = useQueryClient();

  useEffect(() => {
    if (!storyId || !active) return;
    let cancelled = false;

    const ask = async (): Promise<void> => {
      const state = useTurnStore.getState();
      if (!mayStillBeRunning(state) || state.status === 'streaming') return;
      const result = await reconcileTurn(client, storyId, state.chapterNumber);
      if (cancelled) return;
      // `landed` is the good ending: the chapter exists, the queries were invalidated, and
      // the failure the player is looking at was never a failure.
      if (result === 'landed') useTurnStore.getState().reset();
      else useTurnStore.getState().noteRecovery(result);
    };

    void ask();
    const timer = setInterval(() => void ask(), ACTIVE_TURN_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [client, storyId, active]);
}

/**
 * A backgrounded mobile tab can have its stream killed by the OS without an error — the
 * bytes simply stop. On return to visible, a stream that has gone quiet past the heartbeat
 * budget is checked against the server rather than trusted.
 */
function useKilledStreamCheck(storyId: string | undefined, resolving: boolean): void {
  const client = useQueryClient();

  useEffect(() => {
    if (!storyId || !resolving) return;

    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      const state = useTurnStore.getState();
      if (!mayStillBeRunning(state)) return;
      // A live stream is left alone: its heartbeat is proof enough, and re-asking mid-turn
      // would race the commit the stream is about to deliver.
      const silent = Date.now() - (state.lastByteAt ?? 0);
      if (state.status === 'streaming' && silent < TURN_SILENCE_WARNING_MS) return;

      void reconcileTurn(client, storyId, state.chapterNumber).then((result) => {
        if (result === 'landed') useTurnStore.getState().reset();
        else useTurnStore.getState().noteRecovery(result);
      });
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [client, storyId, resolving]);
}

function asApiError(cause: unknown): ApiError {
  if (cause instanceof ApiError) return cause;
  if (cause instanceof DOMException && cause.name === 'TimeoutError') {
    return new ApiError('stream_interrupted', 'The turn stream exceeded the client timeout.');
  }
  return new ApiError('internal', cause instanceof Error ? cause.message : 'The turn failed.');
}
